import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { RSVP, RSVPStatus, DishCategory } from '../types';

export function useRSVPs(eventId: string | undefined) {
  const { ready } = useAuth();
  const [rsvps, setRsvps] = useState<RSVP[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchRSVPs = useCallback(async () => {
    if (!eventId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('rsvps')
        .select(`
          *,
          profile:profiles!user_id (
            id, full_name, address, avatar_url
          )
        `)
        .eq('event_id', eventId)
        .order('created_at', { ascending: true });

      if (!error && data) setRsvps(data as RSVP[]);
    } catch {
      // non-fatal
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (!ready) return;
    fetchRSVPs();
  }, [ready, fetchRSVPs]);

  const upsertRSVP = async (
    userId: string,
    status: RSVPStatus,
    dishName?: string,
    dishCategory?: DishCategory
  ) => {
    if (!eventId) return { error: 'No event ID' };

    const { error } = await supabase.from('rsvps').upsert(
      {
        event_id: eventId,
        user_id: userId,
        status,
        dish_name: dishName || null,
        dish_category: dishCategory || null,
      },
      { onConflict: 'event_id,user_id' }
    );

    if (!error) await fetchRSVPs();
    return { error: error?.message ?? null };
  };

  const myRSVP = (userId: string) =>
    rsvps.find((r) => r.user_id === userId) ?? null;

  const comingCount = rsvps.filter((r) => r.status === 'coming').length;

  return { rsvps, loading, upsertRSVP, myRSVP, comingCount, refetch: fetchRSVPs };
}
